const DEFAULT_TIMEZONE = 'Asia/Tokyo';

export function formatDateTime(
  value: any,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  timeStyle: 'medium' | 'full' | 'long' | 'short' | undefined = 'medium',
): string {
  if (!value) {
    return '';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    dateStyle: 'medium',
    timeStyle: timeStyle,
    timeZone: timeZone,
  }).format(new Date(value));
}

export function formatDate(
  value: any,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  dateStyle: 'medium' | 'full' | 'long' | 'short' | undefined = 'medium',
): string {
  if (!value) {
    return '';
  }

  const formatted = new Intl.DateTimeFormat('ja-JP', {
    dateStyle: dateStyle,
    timeZone: timeZone,
  }).format(new Date(value));

  return formatted.replace(/\//g, '-');
}

export function formatTime(
  value: any,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  timeStyle: 'medium' | 'full' | 'long' | 'short' | undefined = 'medium',
): string {
  return new Intl.DateTimeFormat('ja-JP', {
    timeStyle: timeStyle,
    timeZone: timeZone,
  }).format(new Date(value));
}

/**
 * Formats a time string (HH:MM:SS or HH:MM) to HH:MM format
 * @param timeString - Time string like "12:00:12" or "12:00"
 * @returns Formatted time as "HH:MM"
 */
export function formatTimeString(timeString: string): string {
  if (!timeString) {
    return '';
  }

  // If it's already in HH:MM format, return as is
  if (timeString.length === 5 && timeString.match(/^\d{2}:\d{2}$/)) {
    return timeString;
  }

  // If it has seconds (HH:MM:SS), remove them
  if (timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
    return timeString.substring(0, 5);
  }

  return timeString;
}

export function todayStr(timeZone?: string | undefined): string {
  if (!timeZone) {
    timeZone = 'Asia/Tokyo';
  }
  const formatted = new Intl.DateTimeFormat('ja-JP', {
    dateStyle: 'medium',
    timeZone: timeZone,
  }).format(new Date());

  return formatted.replace(/\//g, '-');
}

export function daysAgoStr(days: number, timeZone?: string | undefined): string {
  if (!timeZone) {
    timeZone = 'Asia/Tokyo';
  }
  const date = new Date();
  date.setDate(date.getDate() - days);
  const formatted = new Intl.DateTimeFormat('ja-JP', {
    dateStyle: 'medium',
    timeZone: timeZone,
  }).format(date);

  return formatted.replace(/\//g, '-');
}

export function firstDayOfThisMonthStr(timeZone?: string | undefined): String {
  if (!timeZone) {
    timeZone = 'Asia/Tokyo';
  }

  const date = new Date();
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const formatted = new Intl.DateTimeFormat('ja-JP', {
    dateStyle: 'medium',
    timeZone: timeZone,
  }).format(firstDay);

  return formatted.replace(/\//g, '-');
}

export function firstDayOfThisYearStr(timeZone?: string | undefined): string {
  if (!timeZone) {
    timeZone = 'Asia/Tokyo';
  }

  const date = new Date();
  const firstDay = new Date(date.getFullYear(), 0, 1);
  const formatted = new Intl.DateTimeFormat('ja-JP', {
    dateStyle: 'medium',
    timeZone: timeZone,
  }).format(firstDay);

  return formatted.replace(/\//g, '-');
}

export function dateTimeNoTz(date: Date): string {
  return toLocalDate(date).toISOString().slice(0, 16);
}

export function toLocalDate(date: Date): Date {
  const timezoneOffset = date.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
  return new Date(date.getTime() - timezoneOffset);
}

export function epochToLocalDateTime(epoch: number, toUnit: 'day' | 'hour' | 'minute' | 'second' = 'minute'): string {
  const date = new Date(epoch);

  const localDate = toLocalDate(date);

  if (toUnit === 'day') {
    return localDate.toISOString().slice(0, 10);
  }

  if (toUnit === 'hour') {
    return localDate.toISOString().slice(0, 13);
  }

  if (toUnit === 'minute') {
    return localDate.toISOString().slice(0, 16);
  }
  if (toUnit === 'second') {
    return localDate.toISOString().slice(0, 19);
  }
  return localDate.toISOString();
}

/**
 * Converts a string or Date value into an epoch timestamp (milliseconds).
 * @param input - The input value, either a string or a Date object.
 * @returns The epoch timestamp in milliseconds, or null if the input is invalid.
 */
export function toEpoch(input: string | Date | undefined): number | null {
  if (!input) {
    return null;
  }

  try {
    let date: Date;

    if (typeof input === 'string') {
      date = new Date(input);
    } else if (input instanceof Date) {
      date = input;
    } else {
      throw new Error('Invalid input type');
    }

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }

    return date.getTime();
  } catch (error) {
    console.error('Error converting to epoch:', error);
    return null; // Return null if conversion fails
  }
}

/**
 * Given a date, returns the date string of the first day (Monday) of that week.
 * @param date - The input date as a string or Date object.
 * @returns The date string (YYYY-MM-DD) of the first day (Monday) of the week.
 */
export function getFirstDayOfWeek(date: string | Date): string {
  const d = new Date(date);

  // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = d.getDay();

  // Calculate the difference to get to Monday (1)
  // If Sunday (0), go back 6 days; otherwise go back (dayOfWeek - 1) days
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  // Create a new date object for Monday
  const monday = new Date(d);
  monday.setDate(d.getDate() - diff);

  return formatDate(monday);
}

/**
 * Given a date, returns the date string of the first day of that month.
 * @param date - The input date as a string or Date object.
 * @returns The date string (YYYY-MM-DD) of the first day of the month.
 */
export function getFirstDayOfMonth(date: string | Date): string {
  const d = new Date(date);
  const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
  return formatDate(firstDay);
}

/**
 * Given a date, returns the date string of the last day of that month.
 * @param date - The input date as a string or Date object.
 * @returns The date string (YYYY-MM-DD) of the last day of the month.
 */
export function getLastDayOfMonth(date: string | Date): string {
  const d = new Date(date);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return formatDate(lastDay);
}

/**
 * Returns an array of date strings from dateStart to dateEnd (inclusive).
 * @param dateStart - Start date string (YYYY-MM-DD).
 * @param dateEnd - End date string (YYYY-MM-DD).
 * @returns Array of date strings.
 */
export function getDateRangeArray(dateStart: string, dateEnd: string): string[] {
  const start = new Date(dateStart);
  const end = new Date(dateEnd);
  const dates: string[] = [];
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  while (current <= end) {
    dates.push(formatDate(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

export function getDayOfWeek(date: string | Date): DayOfWeek {
  const d = new Date(date);
  switch (d.getDay()) {
    case 0:
      return DayOfWeek.Sunday;
    case 1:
      return DayOfWeek.Monday;
    case 2:
      return DayOfWeek.Tuesday;
    case 3:
      return DayOfWeek.Wednesday;
    case 4:
      return DayOfWeek.Thursday;
    case 5:
      return DayOfWeek.Friday;
    case 6:
      return DayOfWeek.Saturday;
    default:
      throw new Error('Invalid day of week');
  }
}
